import React from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import { getShortFormDate } from '../../helpers/stringHelpers'
import {
    getStatusAccentColor,
    InspectionChipStatus,
    InspectionStatusChip,
} from './InspectionStatusChip'
import { IssuePreviewSummary } from './IssuePreviewSummary'
import { InspectionPreviewItem } from './inspectionUtils'

interface InspectionSummaryCardProps {
    icon: React.ReactNode
    title: string
    status: InspectionChipStatus
    dueDate?: string | null
    completedDate?: string | null
    areaCount?: number
    previewItems?: InspectionPreviewItem[]
    previewVariant?: 'tenant_issues' | 'flagged' | 'damages'
    onViewResults?: () => void
    viewResultsLabel?: string
    children?: React.ReactNode
}

export const InspectionSummaryCard: React.FC<InspectionSummaryCardProps> = ({
    icon,
    title,
    status,
    dueDate,
    completedDate,
    areaCount = 0,
    previewItems = [],
    previewVariant = 'tenant_issues',
    onViewResults,
    viewResultsLabel = 'View full results',
    children,
}) => {
    const theme = useTheme()
    const accentColor = getStatusAccentColor(status, theme)

    const metadataParts: string[] = []
    if (dueDate) {
        metadataParts.push(`Due ${getShortFormDate(dueDate)}`)
    }
    if (completedDate) {
        metadataParts.push(`Completed ${getShortFormDate(completedDate)}`)
    }
    if (areaCount > 0) {
        metadataParts.push(
            `${areaCount} area${areaCount === 1 ? '' : 's'} inspected`,
        )
    }

    return (
        <Box
            sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    height: 3,
                    background: `linear-gradient(90deg, ${accentColor} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />
            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: metadataParts.length > 0 ? 1.5 : 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ color: accentColor, display: 'flex', flexShrink: 0 }}>
                            {icon}
                        </Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: 'rgba(255,255,255,0.9)',
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <InspectionStatusChip status={status} />
                </Box>

                {metadataParts.length > 0 && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.5)',
                            mb: 1.5,
                            fontSize: '0.8125rem',
                        }}
                    >
                        {metadataParts.join('  ·  ')}
                    </Typography>
                )}

                {children}

                {status === 'complete' && areaCount > 0 && (
                    <Box sx={{ mb: onViewResults ? 2 : 0, mt: children ? 1.5 : 0 }}>
                        <IssuePreviewSummary
                            items={previewItems}
                            areaCount={areaCount}
                            variant={previewVariant}
                        />
                    </Box>
                )}

                {onViewResults && (
                    <Button
                        variant="outlined"
                        fullWidth
                        endIcon={<ChevronRightIcon />}
                        onClick={onViewResults}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: 'rgba(255,255,255,0.85)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: 'rgba(255,255,255,0.05)',
                            },
                        }}
                    >
                        {viewResultsLabel}
                    </Button>
                )}
            </Box>
        </Box>
    )
}
